import React, {
  memo,
  useMemo,
  useContext,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import styled from '@emotion/styled';
import { ArrowUpward, ArrowDownward, SwapVert } from '@mui/icons-material';
import type { API } from '@trinity/chat';
import { AppThemingContext } from '../../containers/AppTheming/Context';
import { QuantAnalysisFilterContext } from '../../containers/QuantAnalysis/context';
import { cx } from '../../utils';
import { pathEquals } from '../../utils/nested-filter';
import {
  AggregationKeywords,
  IntegerRowKeywords,
  parseNumericValue,
  convertColorIntToHex,
  roundCellValue,
} from '../../utils/quant-analysis';
import styles from './style/Analysis.Table.module.css';

type QuantAnalysisSortConfig = API.ProjectsQuantAnalysis.QuantAnalysisSortConfig;
type QuantFileTableData = API.ProjectsQuantAnalysis.QuantFileTableData;
type QuantAnalysisSelection = API.ProjectsQuantAnalysis.QuantAnalysisSelection;

function isIntegerRow(rowNames: string[]): boolean {
  const lastRow = rowNames.at(-1);

  if (!lastRow) return false;

  return IntegerRowKeywords.some((k) => k.toLowerCase() === lastRow.toLowerCase());
}

type Props = {
  className?: string;
  data: QuantFileTableData;
  originalData?: QuantFileTableData;
  headerBackgroundColor?: string;
  initialSortConfig?: QuantAnalysisSortConfig | null;
  disableSorting?: boolean;
  disableSelection?: boolean;
  decimalPlaces?: number;
  onSelectionChange?: (selection: QuantAnalysisSelection) => void;
};

type HeaderCell = {
  value: string;
  colSpan: number;
  rowSpan: number;
};

type RowCell = {
  value: string;
  rowSpan: number;
  isRendered: boolean;
};

const MATCH_THRESHOLD = 0.95;
const ROW_SAMPLE_SIZE = 5;
const DRAG_DEBOUNCE_MS = 100;

function renderArrowIcon(direction: string, color: number): React.ReactElement | null {
  if (direction !== 'Up' && direction !== 'Down') return null;
  const hexColor = convertColorIntToHex(color);
  const Icon = direction === 'Up' ? ArrowUpward : ArrowDownward;
  return (
    <ArrowIcon color={hexColor}>
      <Icon fontSize="small" />
    </ArrowIcon>
  );
}

function arePathsCompatibleAtLevel(path1: string[], path2: string[], level: number): boolean {
  for (let i = 0; i <= level; i++) {
    if ((path1[i] ?? '') !== (path2[i] ?? '')) {
      return false;
    }
  }
  return true;
}

function computeHeaderSpan(data: string[][], startCol: number, level: number): number {
  let span = 1;
  while (startCol + span < data.length) {
    const path1 = data[startCol];
    const path2 = data[startCol + span];
    if (!path1 || !path2) break;
    if (!arePathsCompatibleAtLevel(path1, path2, level)) break;
    if (path1.length > level + 1 !== path2.length > level + 1) break;
    span++;
  }
  return span;
}

function buildHeaderRow(data: string[][], level: number, maxDepth: number): HeaderCell[] {
  const row: HeaderCell[] = [];
  let col = 0;
  while (col < data.length) {
    const cellValue = data[col]?.[level] ?? '';
    const span = computeHeaderSpan(data, col, level);
    const isLeafLevel = data[col]?.length === level + 1;
    row.push({
      value: cellValue,
      colSpan: span,
      rowSpan: isLeafLevel ? maxDepth - level : 1,
    });
    col += span;
  }
  return row;
}

function computeHeaderRows(data: string[][]): HeaderCell[][] {
  if (data.length === 0) return [];
  const maxDepth = Math.max(...data.map((path) => path.length));
  const rows: HeaderCell[][] = [];
  for (let level = 0; level < maxDepth; level++) {
    rows.push(buildHeaderRow(data, level, maxDepth));
  }
  return rows;
}

function allPreviousColumnsMatch(
  data: string[][],
  targetRow: number,
  upToCol: number,
  referenceRow: string[]
): boolean {
  for (let i = 0; i < upToCol; i++) {
    if ((data[targetRow]?.[i] ?? '') !== (referenceRow[i] ?? '')) {
      return false;
    }
  }
  return true;
}

function computeRowCellSpan(
  data: string[][],
  row: number,
  col: number,
  currentValue: string,
  currentRow: string[],
  numRows: number
): number {
  let span = 1;
  for (let k = row + 1; k < numRows; k++) {
    if ((data[k]?.[col] ?? '') !== currentValue) break;
    if (!allPreviousColumnsMatch(data, k, col, currentRow)) break;
    span++;
  }
  return span;
}

function buildRowCell(data: string[][], row: number, col: number, numRows: number): RowCell {
  const currentRow = data[row] || [];
  const currentValue = currentRow[col] ?? '';
  const prevValue = data[row - 1]?.[col] ?? '';

  if (row === 0 || prevValue !== currentValue) {
    const span = computeRowCellSpan(data, row, col, currentValue, currentRow, numRows);
    return { value: currentValue, rowSpan: span, isRendered: true };
  }
  return { value: currentValue, rowSpan: 0, isRendered: false };
}

function computeRowHeaders(data: string[][]): RowCell[][] {
  if (data.length === 0) return [];
  const numCols = Math.max(0, ...data.map((row) => row.length));
  const numRows = data.length;
  const result: RowCell[][] = Array(numRows);

  for (let row = 0; row < numRows; row++) {
    result[row] = Array(numCols);
    for (let col = 0; col < numCols; col++) {
      result[row][col] = buildRowCell(data, row, col, numRows);
    }
  }

  return result;
}

const getDefaultSelection = (): QuantAnalysisSelection => ({
  rows: new Set(),
  columns: new Set(),
  cells: new Map(),
});

function createCellKey(row: number, col: number): string {
  return `${row}-${col}`;
}

function parseCellKeys(cells: Map<string, boolean>): {
  rowIndices: Set<number>;
  colIndices: Set<number>;
  rowCellCounts: Map<number, number>;
  colCellCounts: Map<number, number>;
} {
  const rowIndices = new Set<number>();
  const colIndices = new Set<number>();
  const rowCellCounts = new Map<number, number>();
  const colCellCounts = new Map<number, number>();

  for (const cellKey of cells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) continue;

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) continue;
    if (rowIndex < 0 || colIndex < 0) continue;

    rowIndices.add(rowIndex);
    colIndices.add(colIndex);
    rowCellCounts.set(rowIndex, (rowCellCounts.get(rowIndex) || 0) + 1);
    colCellCounts.set(colIndex, (colCellCounts.get(colIndex) || 0) + 1);
  }

  return { rowIndices, colIndices, rowCellCounts, colCellCounts };
}

function checkRowsComplete(
  rowIndices: Set<number>,
  rowCellCounts: Map<number, number>,
  records: (string | number)[][]
): boolean {
  for (const rowIndex of rowIndices) {
    const expectedCellCount = records[rowIndex]?.length ?? 0;
    const actualCellCount = rowCellCounts.get(rowIndex) || 0;
    if (expectedCellCount === 0 || actualCellCount !== expectedCellCount) {
      return false;
    }
  }
  return true;
}

function checkColumnsComplete(
  colIndices: Set<number>,
  colCellCounts: Map<number, number>,
  records: (string | number)[][]
): boolean {
  for (const colIndex of colIndices) {
    let expectedCellCount = 0;
    for (let row = 0; row < records.length; row++) {
      if (records[row] && colIndex < records[row].length) {
        expectedCellCount++;
      }
    }
    const actualCellCount = colCellCounts.get(colIndex) || 0;
    if (expectedCellCount === 0 || actualCellCount !== expectedCellCount) {
      return false;
    }
  }
  return true;
}

function detectCompleteRowsOrColumns(
  cells: Map<string, boolean>,
  records: (string | number)[][]
): { isCompleteRows: boolean; isCompleteColumns: boolean; rowIndices: Set<number>; colIndices: Set<number> } {
  if (cells.size === 0) {
    return { isCompleteRows: false, isCompleteColumns: false, rowIndices: new Set(), colIndices: new Set() };
  }

  const { rowIndices, colIndices, rowCellCounts, colCellCounts } = parseCellKeys(cells);

  if (rowIndices.size === 0 || colIndices.size === 0) {
    return { isCompleteRows: false, isCompleteColumns: false, rowIndices: new Set(), colIndices: new Set() };
  }

  const totalColumns = records[0]?.length ?? 0;
  const totalRows = records.length;

  if (totalColumns === 0 || totalRows === 0) {
    return { isCompleteRows: false, isCompleteColumns: false, rowIndices: new Set(), colIndices: new Set() };
  }

  const isCompleteRows = checkRowsComplete(rowIndices, rowCellCounts, records);
  const isCompleteColumns = checkColumnsComplete(colIndices, colCellCounts, records);

  return { isCompleteRows, isCompleteColumns, rowIndices, colIndices };
}

function buildSelectionForColumnFilters(
  filteredColumnNames: string[][],
  records: (string | number)[][],
  selection: QuantAnalysisSelection
): void {
  for (let colIndex = 0; colIndex < filteredColumnNames.length; colIndex++) {
    selection.columns.add(colIndex);
    for (let row = 0; row < records.length; row++) {
      selection.cells.set(createCellKey(row, colIndex), true);
    }
  }
}

function buildSelectionForRowFilters(
  filteredRowNames: string[][],
  records: (string | number)[][],
  selection: QuantAnalysisSelection
): void {
  for (let rowIndex = 0; rowIndex < filteredRowNames.length; rowIndex++) {
    selection.rows.add(rowIndex);
    const rowLength = records[rowIndex]?.length ?? 0;
    for (let col = 0; col < rowLength; col++) {
      selection.cells.set(createCellKey(rowIndex, col), true);
    }
  }
}

function buildRowRangeSelection(
  start: number,
  end: number,
  records: (string | number)[][]
): { rows: Set<number>; cells: Map<string, boolean> } {
  const rows = new Set<number>();
  const cells = new Map<string, boolean>();
  for (let i = start; i <= end; i++) {
    rows.add(i);
    const rowLength = records[i]?.length ?? 0;
    for (let col = 0; col < rowLength; col++) {
      cells.set(createCellKey(i, col), true);
    }
  }
  return { rows, cells };
}

function buildColumnRangeSelection(
  start: number,
  end: number,
  records: (string | number)[][]
): { columns: Set<number>; cells: Map<string, boolean> } {
  const columns = new Set<number>();
  const cells = new Map<string, boolean>();
  const recordsLength = records.length;
  for (let i = start; i <= end; i++) {
    columns.add(i);
    for (let row = 0; row < recordsLength; row++) {
      cells.set(createCellKey(row, i), true);
    }
  }
  return { columns, cells };
}

function buildCellRangeSelection(
  startRow: number,
  endRow: number,
  startCol: number,
  endCol: number
): Map<string, boolean> {
  const cells = new Map<string, boolean>();
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      cells.set(createCellKey(r, c), true);
    }
  }
  return cells;
}

function computeFinalNameArray(newNameParts: string[], originalName: string[]): string[] {
  if (newNameParts.length === 1 && originalName.length > 1) {
    return [...originalName.slice(0, -1), newNameParts[0]];
  }
  if (newNameParts.length < originalName.length) {
    const missingLevels = originalName.length - newNameParts.length;
    return [...originalName.slice(0, missingLevels), ...newNameParts];
  }
  return newNameParts;
}

function findBestColumnMatch(
  filteredRecords: (string | number)[][],
  filteredIndex: number,
  originalRecords: (string | number)[][],
  originalColsCount: number,
  usedOriginalIndices: Set<number>
): { origIndex: number; matchRatio: number } | null {
  const filteredColumnData: (string | number)[] = [];
  for (let i = 0; i < filteredRecords.length; i++) {
    const val = filteredRecords[i]?.[filteredIndex];
    if (val !== undefined && val !== null) {
      filteredColumnData.push(val);
    }
  }
  if (filteredColumnData.length === 0) return null;

  let bestMatch: { origIndex: number; matchRatio: number } | null = null;

  for (let origIndex = 0; origIndex < originalColsCount; origIndex++) {
    if (usedOriginalIndices.has(origIndex)) continue;

    const matchRatio = computeColumnMatchRatio(originalRecords, origIndex, filteredColumnData);
    if (matchRatio === null) continue;

    if (!bestMatch || matchRatio > bestMatch.matchRatio) {
      bestMatch = { origIndex, matchRatio };
    }
  }

  return bestMatch;
}

function computeColumnMatchRatio(
  originalRecords: (string | number)[][],
  origIndex: number,
  filteredColumnData: (string | number)[]
): number | null {
  const originalColumnData: (string | number)[] = [];
  for (let i = 0; i < originalRecords.length; i++) {
    const val = originalRecords[i]?.[origIndex];
    if (val !== undefined && val !== null) {
      originalColumnData.push(val);
    }
  }
  if (originalColumnData.length === 0) return null;

  const minLength = Math.min(originalColumnData.length, filteredColumnData.length);
  if (minLength === 0) return null;

  let matches = 0;
  for (let i = 0; i < minLength; i++) {
    if (String(originalColumnData[i]) === String(filteredColumnData[i])) {
      matches++;
    }
  }
  return matches / minLength;
}

function parseDragTarget(
  row: HTMLElement,
  cell: HTMLElement,
  dragMode: 'cell' | 'row' | 'column' | null
): { row: number; col: number } | null {
  const rowIndex = row.dataset.rowIndex ? parseInt(row.dataset.rowIndex, 10) : null;
  const cellIndex = cell.dataset.cellIndex ? parseInt(cell.dataset.cellIndex, 10) : null;
  const columnIndex = cell.dataset.columnIndex ? parseInt(cell.dataset.columnIndex, 10) : null;

  if (dragMode === 'cell' && rowIndex !== null && cellIndex !== null) {
    return { row: rowIndex, col: cellIndex };
  }
  if (dragMode === 'row' && rowIndex !== null) {
    return { row: rowIndex, col: -1 };
  }
  if (dragMode === 'column' && columnIndex !== null) {
    return { row: -1, col: columnIndex };
  }
  return null;
}

function applyRowDragSelection(
  dragStart: { row: number; col: number },
  dragEnd: { row: number; col: number },
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  const start = Math.min(dragStart.row, dragEnd.row);
  const end = Math.max(dragStart.row, dragEnd.row);
  const { rows, cells } = buildRowRangeSelection(start, end, records);
  lastSelectedCellRef.current = null;
  updateSelection({ rows, columns: new Set(), cells, lastSelectedRow: dragEnd.row });
}

function applyColumnDragSelection(
  dragStart: { row: number; col: number },
  dragEnd: { row: number; col: number },
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  const start = Math.min(dragStart.col, dragEnd.col);
  const end = Math.max(dragStart.col, dragEnd.col);
  const { columns, cells } = buildColumnRangeSelection(start, end, records);
  lastSelectedCellRef.current = null;
  updateSelection({ rows: new Set(), columns, cells, lastSelectedColumn: dragEnd.col });
}

function applyCellDragAsRowSelection(
  rowIndices: Set<number>,
  endRow: number,
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  const newRows = new Set(rowIndices);
  const completeCells = new Map<string, boolean>();
  for (const rowIndex of rowIndices) {
    const rowLength = records[rowIndex]?.length ?? 0;
    for (let col = 0; col < rowLength; col++) {
      completeCells.set(createCellKey(rowIndex, col), true);
    }
  }
  lastSelectedCellRef.current = null;
  updateSelection({ rows: newRows, columns: new Set(), cells: completeCells, lastSelectedRow: endRow });
}

function applyCellDragAsColumnSelection(
  colIndices: Set<number>,
  endCol: number,
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  const newColumns = new Set(colIndices);
  const completeCells = new Map<string, boolean>();
  const recordsLength = records.length;
  for (const colIndex of colIndices) {
    for (let row = 0; row < recordsLength; row++) {
      completeCells.set(createCellKey(row, colIndex), true);
    }
  }
  lastSelectedCellRef.current = null;
  updateSelection({ rows: new Set(), columns: newColumns, cells: completeCells, lastSelectedColumn: endCol });
}

function applyCellDragSelection(
  dragStart: { row: number; col: number },
  dragEnd: { row: number; col: number },
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  const startRow = Math.min(dragStart.row, dragEnd.row);
  const endRow = Math.max(dragStart.row, dragEnd.row);
  const startCol = Math.min(dragStart.col, dragEnd.col);
  const endCol = Math.max(dragStart.col, dragEnd.col);
  const newCells = buildCellRangeSelection(startRow, endRow, startCol, endCol);

  const { isCompleteRows, isCompleteColumns, rowIndices, colIndices } = detectCompleteRowsOrColumns(
    newCells,
    records
  );

  if (isCompleteRows && !isCompleteColumns) {
    applyCellDragAsRowSelection(rowIndices, endRow, records, updateSelection, lastSelectedCellRef);
  } else if (isCompleteColumns && !isCompleteRows) {
    applyCellDragAsColumnSelection(colIndices, endCol, records, updateSelection, lastSelectedCellRef);
  } else {
    updateSelection({ rows: new Set(), columns: new Set(), cells: newCells });
  }
}

function applyDragSelection(
  dragMode: 'cell' | 'row' | 'column' | null,
  dragStart: { row: number; col: number },
  dragEnd: { row: number; col: number },
  records: (string | number)[][],
  updateSelection: (s: QuantAnalysisSelection) => void,
  lastSelectedCellRef: React.MutableRefObject<{ row: number; col: number } | null>
): void {
  if (dragMode === 'row' && dragStart.row !== -1 && dragEnd.row !== -1) {
    applyRowDragSelection(dragStart, dragEnd, records, updateSelection, lastSelectedCellRef);
    return;
  }
  if (dragMode === 'column' && dragStart.col !== -1 && dragEnd.col !== -1) {
    applyColumnDragSelection(dragStart, dragEnd, records, updateSelection, lastSelectedCellRef);
    return;
  }
  if (
    dragMode === 'cell' &&
    dragStart.row !== -1 &&
    dragStart.col !== -1 &&
    dragEnd.row !== -1 &&
    dragEnd.col !== -1
  ) {
    applyCellDragSelection(dragStart, dragEnd, records, updateSelection, lastSelectedCellRef);
  }
}

function isCellSelected(
  rowIndex: number,
  cellIndex: number,
  isRowSelected: boolean,
  selection: QuantAnalysisSelection,
  isDragging: boolean,
  dragMode: 'cell' | 'row' | 'column' | null,
  dragStart: { row: number; col: number } | null,
  dragEnd: { row: number; col: number } | null
): boolean {
  if (isRowSelected || selection.columns.has(cellIndex)) return true;

  const cellKey = createCellKey(rowIndex, cellIndex);
  if (selection.cells.has(cellKey)) return true;

  if (isDragging && dragMode === 'cell' && dragStart && dragEnd) {
    const minRow = Math.min(dragStart.row, dragEnd.row);
    const maxRow = Math.max(dragStart.row, dragEnd.row);
    const minCol = Math.min(dragStart.col, dragEnd.col);
    const maxCol = Math.max(dragStart.col, dragEnd.col);
    return rowIndex >= minRow && rowIndex <= maxRow && cellIndex >= minCol && cellIndex <= maxCol;
  }

  return false;
}

function isSortIconTarget(target: HTMLElement): boolean {
  return (
    target.closest('[class*="SortIconContainer"]') !== null ||
    target.tagName === 'svg' ||
    target.tagName === 'path'
  );
}

function getThemePaletteColors(
  theming: {
    enabled?: boolean;
    palette?: { primary?: { light?: string; main?: string }; white?: { main?: string } };
  } | null
) {
  return {
    enabled: theming?.enabled,
    primaryLight: theming?.palette?.primary?.light,
    primaryMain: theming?.palette?.primary?.main,
  };
}

function computeColumnDragRange(
  isDragging: boolean,
  dragMode: 'cell' | 'row' | 'column' | null,
  dragStart: { row: number; col: number } | null,
  dragEnd: { row: number; col: number } | null
): { min: number; max: number } | null {
  if (!isDragging || dragMode !== 'column' || !dragStart || !dragEnd) return null;
  return { min: Math.min(dragStart.col, dragEnd.col), max: Math.max(dragStart.col, dragEnd.col) };
}

function toggleRowInSelection(
  rowIndex: number,
  rowLength: number,
  currentSelection: QuantAnalysisSelection
): QuantAnalysisSelection {
  const newRows = new Set(currentSelection.rows);
  const newColumns = new Set(currentSelection.columns);
  const newCells = new Map(currentSelection.cells);

  if (newRows.has(rowIndex)) {
    newRows.delete(rowIndex);
    for (let col = 0; col < rowLength; col++) {
      if (!newColumns.has(col)) {
        newCells.delete(createCellKey(rowIndex, col));
      }
    }
  } else {
    newRows.add(rowIndex);
    for (let col = 0; col < rowLength; col++) {
      newCells.set(createCellKey(rowIndex, col), true);
    }
  }

  return { rows: newRows, columns: newColumns, cells: newCells, lastSelectedRow: rowIndex };
}

function computeNameEdit(
  editingIndex: number,
  editingValue: string,
  indexMap: Map<number, number>,
  originalNames: (string | null)[][]
): { originalIndex: number; finalNameArray: string[] } | null {
  const trimmedValue = editingValue.trim();
  if (trimmedValue.length === 0) return null;

  const originalIndex = indexMap.get(editingIndex);
  if (originalIndex === undefined) return null;

  const originalName = originalNames[originalIndex] ?? [];
  const newNameParts = trimmedValue.split(' - ').filter((s) => s.length > 0);
  const finalNameArray = computeFinalNameArray(newNameParts, originalName);

  if (finalNameArray.length === 0) return null;
  return { originalIndex, finalNameArray };
}

export const AnalysisTable = memo(
  forwardRef<HTMLTableElement, Props>((props, ref) => {
    const { columnNames, records, rowNames, metadata } = props.data;

    const [internalSortConfig, setInternalSortConfig] = useState<QuantAnalysisSortConfig | null>(
      props.initialSortConfig ?? null
    );

    const contextValue = useContext(QuantAnalysisFilterContext);
    const [quantFilter, setQuantFilter] = contextValue || [null, null];

    useEffect(() => {
      if (!contextValue && props.initialSortConfig !== undefined) {
        setInternalSortConfig(props.initialSortConfig);
      }
    }, [props.initialSortConfig, contextValue]);

    const sortConfig = quantFilter?.sortConfig ?? internalSortConfig;

    const [internalSelection, setInternalSelection] = useState<QuantAnalysisSelection>(getDefaultSelection);

    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null);
    const [dragEnd, setDragEnd] = useState<{ row: number; col: number } | null>(null);
    const [dragMode, setDragMode] = useState<'cell' | 'row' | 'column' | null>(null);
    const justFinishedDragRef = useRef(false);

    useEffect(() => {
      if (!contextValue) {
        setInternalSelection(getDefaultSelection());
      }
    }, [contextValue]);

    const tableRef = useRef<HTMLTableElement>(null);

    const filteredColumnNames = useMemo(() => {
      return columnNames.map((col) => col.filter((cell) => cell !== null)).filter((col) => col.length > 0);
    }, [columnNames]);

    const filteredRowNames = useMemo(() => {
      return rowNames.map((row) => row.filter((cell) => cell !== null)).filter((row) => row.length > 0);
    }, [rowNames]);

    const prevFiltersRef = useRef({
      columnNames: quantFilter?.columnNames ?? [],
      rowNames: quantFilter?.rowNames ?? [],
    });
    useEffect(() => {
      if (!quantFilter || !contextValue || !setQuantFilter) return;

      const prevFilters = prevFiltersRef.current;
      const columnNamesChanged =
        prevFilters.columnNames.length !== quantFilter.columnNames.length ||
        prevFilters.columnNames.some((name, i) => !pathEquals(name, quantFilter.columnNames[i]));
      const rowNamesChanged =
        prevFilters.rowNames.length !== quantFilter.rowNames.length ||
        prevFilters.rowNames.some((name, i) => !pathEquals(name, quantFilter.rowNames[i]));

      if (!columnNamesChanged && !rowNamesChanged) return;

      const newSelection = getDefaultSelection();
      if (quantFilter.columnNames.length > 0) {
        buildSelectionForColumnFilters(filteredColumnNames, records, newSelection);
      }
      if (quantFilter.rowNames.length > 0) {
        buildSelectionForRowFilters(filteredRowNames, records, newSelection);
      }

      setQuantFilter((current) => {
        if (!current) return current;
        return { ...current, selection: newSelection };
      });

      prevFiltersRef.current = {
        columnNames: [...quantFilter.columnNames],
        rowNames: [...quantFilter.rowNames],
      };
    }, [quantFilter, contextValue, setQuantFilter, filteredColumnNames, filteredRowNames, records]);

    const effectiveSelection = props.disableSelection
      ? getDefaultSelection()
      : (quantFilter?.selection ?? internalSelection);
    const selection = effectiveSelection;

    const theming = useContext(AppThemingContext);
    const { enabled, primaryLight, primaryMain } = getThemePaletteColors(theming);
    const selectionBg = useMemo(
      () => (enabled ? (primaryLight ?? '#E5F5FC') : '#E5F5FC'),
      [enabled, primaryLight]
    );
    const selectionBorder = useMemo(
      () => (enabled ? (primaryMain ?? '#28aae1') : '#28aae1'),
      [enabled, primaryMain]
    );

    const updateSelection = useCallback(
      (newSelection: QuantAnalysisSelection) => {
        if (props.disableSelection) return;

        if (contextValue && setQuantFilter) {
          setQuantFilter((current) => {
            if (!current) return current;
            return { ...current, selection: newSelection };
          });
        } else {
          setInternalSelection(newSelection);
        }

        props.onSelectionChange?.(newSelection);
      },
      [props.disableSelection, props.onSelectionChange, contextValue, setQuantFilter]
    );

    const handleRowMouseDown = useCallback(
      (rowIndex: number, event: React.MouseEvent) => {
        if (props.disableSelection || event.button !== 0) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) return;

        event.preventDefault();
        setIsDragging(true);
        setDragStart({ row: rowIndex, col: -1 });
        setDragEnd({ row: rowIndex, col: -1 });
        setDragMode('row');
      },
      [props.disableSelection]
    );

    const lastSelectedCellRef = useRef<{ row: number; col: number } | null>(null);

    const handleRowClick = useCallback(
      (rowIndex: number, event: React.MouseEvent) => {
        if (props.disableSelection || isDragging || justFinishedDragRef.current) return;

        const currentSelection = selection;
        const rowLength = records[rowIndex]?.length ?? 0;
        if (rowLength === 0) return;

        if (event.ctrlKey || event.metaKey) {
          const newSelection = toggleRowInSelection(rowIndex, rowLength, currentSelection);
          lastSelectedCellRef.current = null;
          updateSelection(newSelection);
          return;
        }

        if (event.shiftKey && currentSelection.lastSelectedRow !== undefined) {
          const start = Math.min(currentSelection.lastSelectedRow, rowIndex);
          const end = Math.max(currentSelection.lastSelectedRow, rowIndex);
          const { rows, cells } = buildRowRangeSelection(start, end, records);
          updateSelection({ rows, columns: new Set(), cells, lastSelectedRow: rowIndex });
          return;
        }

        const newCells = new Map<string, boolean>();
        for (let col = 0; col < rowLength; col++) {
          newCells.set(createCellKey(rowIndex, col), true);
        }
        lastSelectedCellRef.current = null;
        updateSelection({
          rows: new Set([rowIndex]),
          columns: new Set(),
          cells: newCells,
          lastSelectedRow: rowIndex,
        });
      },
      [props.disableSelection, selection, updateSelection, isDragging, records]
    );

    const handleColumnMouseDown = useCallback(
      (columnIndex: number, event: React.MouseEvent) => {
        if (props.disableSelection || event.button !== 0) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) return;

        event.preventDefault();
        setIsDragging(true);
        setDragStart({ row: -1, col: columnIndex });
        setDragEnd({ row: -1, col: columnIndex });
        setDragMode('column');
      },
      [props.disableSelection]
    );

    const handleColumnClick = useCallback(
      (columnIndex: number, colSpan: number, event: React.MouseEvent) => {
        if (props.disableSelection || isDragging || justFinishedDragRef.current) return;

        const currentSelection = selection;
        const recordsLength = records.length;
        const columnIndices: number[] = [];
        for (let i = 0; i < colSpan; i++) {
          columnIndices.push(columnIndex + i);
        }

        if (event.ctrlKey || event.metaKey) {
          const newColumns = new Set(currentSelection.columns);
          const newRows = new Set(currentSelection.rows);
          const newCells = new Map(currentSelection.cells);

          const allSelected = columnIndices.every((idx) => newColumns.has(idx));
          if (allSelected) {
            columnIndices.forEach((idx) => {
              newColumns.delete(idx);
              for (let row = 0; row < recordsLength; row++) {
                if (!newRows.has(row)) newCells.delete(createCellKey(row, idx));
              }
            });
          } else {
            columnIndices.forEach((idx) => {
              newColumns.add(idx);
              for (let row = 0; row < recordsLength; row++) {
                newCells.set(createCellKey(row, idx), true);
              }
            });
          }
          lastSelectedCellRef.current = null;
          updateSelection({
            rows: newRows,
            columns: newColumns,
            cells: newCells,
            lastSelectedColumn: columnIndex,
          });
          return;
        }

        if (event.shiftKey && currentSelection.lastSelectedColumn !== undefined) {
          const start = Math.min(currentSelection.lastSelectedColumn, columnIndex);
          const end = Math.max(currentSelection.lastSelectedColumn, columnIndex + colSpan - 1);
          const { columns, cells } = buildColumnRangeSelection(start, end, records);
          updateSelection({ rows: new Set(), columns, cells, lastSelectedColumn: columnIndex });
          return;
        }

        const { columns, cells } = buildColumnRangeSelection(columnIndex, columnIndex + colSpan - 1, records);
        lastSelectedCellRef.current = null;
        updateSelection({ rows: new Set(), columns, cells, lastSelectedColumn: columnIndex });
      },
      [props.disableSelection, selection, updateSelection, isDragging, records]
    );

    const handleCellMouseDown = useCallback(
      (rowIndex: number, cellIndex: number, event: React.MouseEvent) => {
        if (props.disableSelection || event.button !== 0) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) return;

        event.preventDefault();
        setIsDragging(true);
        setDragStart({ row: rowIndex, col: cellIndex });
        setDragEnd({ row: rowIndex, col: cellIndex });
        setDragMode('cell');
      },
      [props.disableSelection]
    );

    const handleCellClick = useCallback(
      (rowIndex: number, cellIndex: number, event: React.MouseEvent) => {
        if (props.disableSelection || isDragging || justFinishedDragRef.current) return;

        const currentSelection = selection;
        const cellKey = createCellKey(rowIndex, cellIndex);

        if (event.ctrlKey || event.metaKey) {
          const newCells = new Map(currentSelection.cells);
          const newRows = new Set(currentSelection.rows);
          const newColumns = new Set(currentSelection.columns);

          if (newCells.has(cellKey)) {
            newCells.delete(cellKey);
          } else {
            newCells.set(cellKey, true);
          }
          lastSelectedCellRef.current = { row: rowIndex, col: cellIndex };
          updateSelection({ rows: newRows, columns: newColumns, cells: newCells });
          return;
        }

        if (event.shiftKey && lastSelectedCellRef.current) {
          const { row: firstRow, col: firstCol } = lastSelectedCellRef.current;
          const startRow = Math.min(firstRow, rowIndex);
          const endRow = Math.max(firstRow, rowIndex);
          const startCol = Math.min(firstCol, cellIndex);
          const endCol = Math.max(firstCol, cellIndex);
          const newCells = buildCellRangeSelection(startRow, endRow, startCol, endCol);
          updateSelection({ rows: new Set(), columns: new Set(), cells: newCells });
          return;
        }

        lastSelectedCellRef.current = { row: rowIndex, col: cellIndex };
        updateSelection({ rows: new Set(), columns: new Set(), cells: new Map([[cellKey, true]]) });
      },
      [props.disableSelection, selection, updateSelection, isDragging]
    );

    const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null);
    const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const editingInputRef = useRef<HTMLInputElement>(null);

    const originalData = props.originalData ?? props.data;
    const columnIndexMap = useMemo(() => {
      const map = new Map<number, number>();
      if (originalData === props.data) {
        filteredColumnNames.forEach((_, index) => {
          map.set(index, index);
        });
        return map;
      }

      const originalCols = originalData.columnNames
        .map((col) => col.filter((cell) => cell !== null))
        .filter((col) => col.length > 0);
      const originalRecords = originalData.records;
      const filteredRecords = props.data.records;
      const usedOriginalIndices = new Set<number>();

      filteredColumnNames.forEach((_, filteredIndex) => {
        const bestMatch = findBestColumnMatch(
          filteredRecords,
          filteredIndex,
          originalRecords,
          originalCols.length,
          usedOriginalIndices
        );

        if (bestMatch && bestMatch.matchRatio >= MATCH_THRESHOLD) {
          map.set(filteredIndex, bestMatch.origIndex);
          usedOriginalIndices.add(bestMatch.origIndex);
        }
      });

      return map;
    }, [filteredColumnNames, props.data.records, originalData, props.data]);

    const rowIndexMap = useMemo(() => {
      const map = new Map<number, number>();
      if (originalData === props.data) {
        filteredRowNames.forEach((_, index) => {
          map.set(index, index);
        });
        return map;
      }

      const originalRows = originalData.rowNames
        .map((row) => row.filter((cell) => cell !== null))
        .filter((row) => row.length > 0);
      const originalRecords = originalData.records;

      filteredRowNames.forEach((_, filteredIndex) => {
        const filteredRowData = records[filteredIndex];
        if (!filteredRowData || filteredRowData.length === 0) return;

        for (let origIndex = 0; origIndex < originalRows.length; origIndex++) {
          const originalRowData = originalRecords[origIndex];
          if (!originalRowData || originalRowData.length === 0) continue;

          const sampleSize = Math.min(ROW_SAMPLE_SIZE, originalRowData.length, filteredRowData.length);
          if (sampleSize === 0) continue;

          let matches = 0;
          for (let i = 0; i < sampleSize; i++) {
            if (originalRowData[i] === filteredRowData[i]) {
              matches++;
            }
          }

          if (matches === sampleSize || (sampleSize > 2 && matches >= sampleSize - 1)) {
            map.set(filteredIndex, origIndex);
            break;
          }
        }
      });

      return map;
    }, [filteredRowNames, records, originalData, props.data]);

    const headerRows = useMemo(() => computeHeaderRows(filteredColumnNames), [filteredColumnNames]);
    const rows = useMemo(() => computeRowHeaders(filteredRowNames), [filteredRowNames]);

    const rowNestingDepth = useMemo(() => {
      if (rows.length === 0) return 0;
      return Math.max(...rows.map((row) => row.filter((cell) => cell.isRendered).length));
    }, [rows]);

    const sortedData = useMemo(() => {
      if (!sortConfig) {
        return { records, rowNames, rows, metadata };
      }

      const nonAggregationRowIndices: number[] = [];
      const aggregationRowIndices: number[] = [];

      filteredRowNames.forEach((row, index) => {
        const isAggregation = AggregationKeywords.some((agg) =>
          row.some((cell: string) => cell.toLowerCase().includes(agg.toLowerCase()))
        );
        if (isAggregation) {
          aggregationRowIndices.push(index);
        } else {
          nonAggregationRowIndices.push(index);
        }
      });

      const nonAggregationRecords = nonAggregationRowIndices.map((idx) => records[idx]).filter(Boolean);
      const nonAggregationIndexedData = nonAggregationRecords.map((record, index) => {
        const originalIndex = nonAggregationRowIndices[index];
        return {
          record,
          rowName: rowNames[originalIndex] ?? [],
          rowHeader: rows[originalIndex] ?? [],
          metadata: {
            arrowColors: metadata?.arrowColors?.[originalIndex],
            arrowDirection: metadata?.arrowDirection?.[originalIndex],
            isSignificant: metadata?.isSignificant?.[originalIndex],
          },
          originalIndex,
        };
      });

      const aggregationRecords = aggregationRowIndices.map((idx) => records[idx]).filter(Boolean);
      const aggregationIndexData = aggregationRecords.map((record, index) => {
        const originalIndex = aggregationRowIndices[index];
        return {
          record,
          rowName: rowNames[originalIndex] ?? [],
          rowHeader: rows[originalIndex] ?? [],
          metadata: {
            arrowColors: metadata?.arrowColors?.[originalIndex],
            arrowDirection: metadata?.arrowDirection?.[originalIndex],
            isSignificant: metadata?.isSignificant?.[originalIndex],
          },
          originalIndex,
        };
      });

      nonAggregationIndexedData.sort((a, b) => {
        const aValue = a.record[sortConfig.columnIndex];
        const bValue = b.record[sortConfig.columnIndex];
        const aNum = parseNumericValue(aValue);
        const bNum = parseNumericValue(bValue);
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      });

      const combinedData = [...nonAggregationIndexedData, ...aggregationIndexData];

      return {
        records: combinedData.map((item) => item.record),
        rowNames: combinedData.map((item) => item.rowName),
        rows: combinedData.map((item) => item.rowHeader),
        metadata: {
          arrowColors: combinedData.map((item) => item.metadata.arrowColors),
          arrowDirection: combinedData.map((item) => item.metadata.arrowDirection),
          isSignificant: combinedData.map((item) => item.metadata.isSignificant),
        },
      };
    }, [records, rowNames, rows, metadata, sortConfig, filteredRowNames]);

    const handleSortState = useCallback(
      (columnIndex: number) => {
        if (props.disableSorting) return;

        const currentSort = sortConfig;
        let newSortConfig: QuantAnalysisSortConfig | null;

        if (currentSort?.columnIndex === columnIndex) {
          newSortConfig = currentSort.direction === 'asc' ? { columnIndex, direction: 'desc' } : null;
        } else {
          newSortConfig = { columnIndex, direction: 'asc' };
        }

        if (contextValue && setQuantFilter) {
          setQuantFilter((current) => {
            if (!current) return current;
            return { ...current, sortConfig: newSortConfig };
          });
        } else {
          setInternalSortConfig(newSortConfig);
        }
      },
      [props.disableSorting, sortConfig, contextValue, setQuantFilter]
    );

    const handleColumnNameDoubleClick = useCallback(
      (columnIndex: number, e: React.MouseEvent) => {
        if (!contextValue || !setQuantFilter) return;
        e.stopPropagation();
        e.preventDefault();
        const currentName = filteredColumnNames[columnIndex] ?? [];
        setEditingColumnIndex(columnIndex);
        setEditingValue(currentName.join(' - '));
        setEditingRowIndex(null);
      },
      [filteredColumnNames, contextValue, setQuantFilter]
    );

    const handleRowNameDoubleClick = useCallback(
      (rowIndex: number, e: React.MouseEvent) => {
        if (!contextValue || !setQuantFilter) return;
        e.stopPropagation();
        e.preventDefault();
        const currentName = filteredRowNames[rowIndex] ?? [];
        setEditingRowIndex(rowIndex);
        setEditingValue(currentName.join(' - '));
        setEditingColumnIndex(null);
      },
      [filteredRowNames, contextValue, setQuantFilter]
    );

    const handleSaveEdit = useCallback(() => {
      if (!contextValue || !setQuantFilter) return;

      if (editingColumnIndex !== null) {
        const result = computeNameEdit(
          editingColumnIndex,
          editingValue,
          columnIndexMap,
          originalData.columnNames
        );
        if (result) {
          setQuantFilter((current) => {
            if (!current) return current;
            const newCustomColumnNames = new Map(current.customColumnNames ?? new Map());
            newCustomColumnNames.set(result.originalIndex, result.finalNameArray);
            return { ...current, customColumnNames: newCustomColumnNames };
          });
        }
        setEditingColumnIndex(null);
      } else if (editingRowIndex !== null) {
        const result = computeNameEdit(editingRowIndex, editingValue, rowIndexMap, originalData.rowNames);
        if (result) {
          setQuantFilter((current) => {
            if (!current) return current;
            const newCustomRowNames = new Map(current.customRowNames ?? new Map());
            newCustomRowNames.set(result.originalIndex, result.finalNameArray);
            return { ...current, customRowNames: newCustomRowNames };
          });
        }
        setEditingRowIndex(null);
      } else {
        // nothing to save
      }
      setEditingValue('');
    }, [
      editingColumnIndex,
      editingRowIndex,
      editingValue,
      contextValue,
      setQuantFilter,
      columnIndexMap,
      rowIndexMap,
      originalData,
    ]);

    const handleCancelEdit = useCallback(() => {
      setEditingColumnIndex(null);
      setEditingRowIndex(null);
      setEditingValue('');
    }, []);

    useEffect(() => {
      if ((editingColumnIndex !== null || editingRowIndex !== null) && editingInputRef.current) {
        editingInputRef.current.focus();
        editingInputRef.current.select();
      }
    }, [editingColumnIndex, editingRowIndex]);

    const columnIndexCacheRef = useRef<Map<number, number>>(new Map());
    const lastHeaderRowsRef = useRef(headerRows);

    if (lastHeaderRowsRef.current !== headerRows) {
      columnIndexCacheRef.current.clear();
      lastHeaderRowsRef.current = headerRows;
    }

    const getColumnIndex = useCallback(
      (cellIndex: number): number => {
        const cached = columnIndexCacheRef.current.get(cellIndex);
        if (cached !== undefined) return cached;

        let actualIndex = 0;
        const lastRow = headerRows[headerRows.length - 1];
        if (lastRow) {
          for (let i = 0; i < cellIndex && i < lastRow.length; i++) {
            actualIndex += lastRow[i]?.colSpan ?? 1;
          }
        }

        columnIndexCacheRef.current.set(cellIndex, actualIndex);
        return actualIndex;
      },
      [headerRows]
    );

    const mouseMoveTimeoutRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
    const lastMouseMoveRef = useRef<{ row: number; col: number } | null>(null);

    useEffect(() => {
      if (!isDragging || !dragStart) return;

      const handleMouseMove = (e: MouseEvent) => {
        if (!tableRef.current) return;

        const target = e.target as HTMLElement;
        const cell = target.closest<HTMLElement>('td, th');
        if (!cell) return;

        const row = cell.closest('tr') as HTMLElement;
        if (!row) return;

        const newDragEnd = parseDragTarget(row, cell, dragMode);

        if (
          newDragEnd &&
          (!lastMouseMoveRef.current ||
            lastMouseMoveRef.current.row !== newDragEnd.row ||
            lastMouseMoveRef.current.col !== newDragEnd.col)
        ) {
          lastMouseMoveRef.current = newDragEnd;

          if (mouseMoveTimeoutRef.current !== null) {
            cancelAnimationFrame(mouseMoveTimeoutRef.current);
          }

          mouseMoveTimeoutRef.current = requestAnimationFrame(() => {
            setDragEnd(newDragEnd);
            mouseMoveTimeoutRef.current = null;
          });
        }
      };

      const handleMouseUp = () => {
        const hadDrag =
          dragStart && dragEnd && (dragStart.row !== dragEnd.row || dragStart.col !== dragEnd.col);

        if (hadDrag && dragEnd && !props.disableSelection) {
          applyDragSelection(dragMode, dragStart, dragEnd, records, updateSelection, lastSelectedCellRef);
          justFinishedDragRef.current = true;
          setTimeout(() => {
            justFinishedDragRef.current = false;
          }, DRAG_DEBOUNCE_MS);
        } else {
          justFinishedDragRef.current = false;
        }

        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setDragMode(null);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        if (mouseMoveTimeoutRef.current !== null) {
          cancelAnimationFrame(mouseMoveTimeoutRef.current);
          mouseMoveTimeoutRef.current = null;
        }
        lastMouseMoveRef.current = null;
      };
    }, [isDragging, dragStart, dragEnd, dragMode, props.disableSelection, updateSelection, records]);

    const shouldFreezeColumns = filteredColumnNames.length > filteredRowNames.length;
    const shouldFreezeRows = !shouldFreezeColumns;
    const lastHeaderRowIndex = headerRows.length - 1;
    const columnDragRange = computeColumnDragRange(isDragging, dragMode, dragStart, dragEnd);

    return (
      <TableContainer className={props.className}>
        <TableElement
          ref={(node) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            } else {
              // no ref to assign
            }
            tableRef.current = node;
          }}
          className={cx(styles.table)}
          headerBackgroundColor={props.headerBackgroundColor}
        >
          <thead style={shouldFreezeColumns ? { position: 'sticky', top: 0, zIndex: 2 } : undefined}>
            {headerRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: rowNestingDepth }).map((_, index) => (
                  <FixedCornerCell
                    key={index}
                    colSpan={1}
                    $freezeColumns={shouldFreezeColumns}
                    $freezeRows={shouldFreezeRows}
                    $colIndex={index}
                    $rowIndex={rowIndex}
                    $rowNestingDepth={rowNestingDepth}
                  />
                ))}
                {row.map((cell, cellIndex) => {
                  const columnIndex = getColumnIndex(cellIndex);
                  const isLastRow = rowIndex === lastHeaderRowIndex;
                  const isSorted = sortConfig?.columnIndex === columnIndex;
                  const isColumnSelected = isLastRow && selection.columns.has(columnIndex);
                  const isColumnInDragRange =
                    isLastRow &&
                    columnDragRange !== null &&
                    columnIndex >= columnDragRange.min &&
                    columnIndex <= columnDragRange.max;
                  const showColumnSelection = isColumnSelected || isColumnInDragRange;

                  return (
                    <SelectableSortableHeaderCell
                      key={cellIndex}
                      data-column-index={isLastRow ? columnIndex : undefined}
                      colSpan={cell.colSpan}
                      rowSpan={cell.rowSpan}
                      $freezeColumns={shouldFreezeColumns}
                      $rowIndex={rowIndex}
                      $isSortable={isLastRow && !props.disableSorting}
                      $isSelected={showColumnSelection}
                      $selectionColor={selectionBg}
                      $selectionBorder={selectionBorder}
                      onMouseDown={(e: React.MouseEvent) => {
                        if (isLastRow && !props.disableSelection) {
                          if (!isSortIconTarget(e.target as HTMLElement)) {
                            handleColumnMouseDown(columnIndex, e);
                          }
                        }
                      }}
                      onMouseEnter={(e: React.MouseEvent) => {
                        if (
                          isDragging &&
                          dragMode === 'column' &&
                          isLastRow &&
                          dragStart &&
                          !props.disableSelection
                        ) {
                          setDragEnd({ row: -1, col: columnIndex });
                        }
                      }}
                      onClick={(e: React.MouseEvent) => {
                        if (!isLastRow || props.disableSelection) return;
                        const target = e.target as HTMLElement;
                        if (isSortIconTarget(target) || target.tagName === 'INPUT') return;
                        handleColumnClick(columnIndex, cell.colSpan, e);
                      }}
                      onDoubleClick={(e: React.MouseEvent) => {
                        if (isLastRow && contextValue) {
                          handleColumnNameDoubleClick(columnIndex, e);
                        }
                      }}
                    >
                      <HeaderContent>
                        {isLastRow && editingColumnIndex === columnIndex ? (
                          <EditableInput
                            ref={editingInputRef}
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={handleSaveEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                editingInputRef.current?.blur();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancelEdit();
                              } else {
                                // other keys are unhandled
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span>{cell.value}</span>
                        )}
                        {isLastRow && !props.disableSorting && (
                          <SortIconContainer
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleSortState(columnIndex);
                            }}
                          >
                            {(() => {
                              if (isSorted) {
                                if (sortConfig?.direction === 'asc') return <ArrowUpward fontSize="small" />;
                                return <ArrowDownward fontSize="small" />;
                              }
                              return <SwapVert fontSize="small" />;
                            })()}
                          </SortIconContainer>
                        )}
                      </HeaderContent>
                    </SelectableSortableHeaderCell>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {(sortedData.rows.length > 0 ? sortedData.rows : sortedData.records.map(() => [])).map((row, rowIndex) => {
              const isRowSelected = selection.rows.has(rowIndex);
              const minDragRow = dragStart && dragEnd ? Math.min(dragStart.row, dragEnd.row) : -1;
              const maxDragRow = dragStart && dragEnd ? Math.max(dragStart.row, dragEnd.row) : -1;
              const isRowInDragRange =
                isDragging && dragMode === 'row' && rowIndex >= minDragRow && rowIndex <= maxDragRow;
              const showRowSelection = isRowSelected || isRowInDragRange;
              const effectiveDecimalPlaces = isIntegerRow(sortedData.rowNames[rowIndex] ?? [])
                ? 0
                : (props.decimalPlaces ?? 0);

              return (
                <SelectableRow
                  key={rowIndex}
                  data-row-index={rowIndex}
                  $isSelected={showRowSelection}
                  $selectionColor={selectionBg}
                  $selectionBorder={selectionBorder}
                  onMouseDown={(e: React.MouseEvent) => {
                    if (!props.disableSelection) {
                      handleRowMouseDown(rowIndex, e);
                    }
                  }}
                  onMouseEnter={(e: React.MouseEvent) => {
                    if (isDragging && dragMode === 'row' && dragStart && !props.disableSelection) {
                      setDragEnd({ row: rowIndex, col: -1 });
                    }
                  }}
                  onClick={(e: React.MouseEvent) => {
                    if (!props.disableSelection) {
                      handleRowClick(rowIndex, e);
                    }
                  }}
                >
                  {row.map(
                    (cell, colIndex) =>
                      cell.isRendered && (
                        <FixedRowHeaderCell
                          className={styles.headerTd}
                          key={colIndex}
                          rowSpan={cell.rowSpan}
                          $freezeRows={shouldFreezeRows}
                          $colIndex={colIndex}
                          $rowNestingDepth={rowNestingDepth}
                          onMouseDown={(e: React.MouseEvent) => {
                            if (!props.disableSelection) {
                              handleRowMouseDown(rowIndex, e);
                            }
                          }}
                          onClick={(e: React.MouseEvent) => {
                            if (!props.disableSelection) {
                              const target = e.target as HTMLElement;
                              if (target.tagName === 'INPUT') {
                                return;
                              }
                              handleRowClick(rowIndex, e);
                            }
                          }}
                          onDoubleClick={(e: React.MouseEvent) => {
                            if (contextValue) {
                              handleRowNameDoubleClick(rowIndex, e);
                            }
                          }}
                        >
                          {editingRowIndex === rowIndex ? (
                            <EditableInput
                              ref={editingInputRef}
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={handleSaveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  editingInputRef.current?.blur();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancelEdit();
                                } else {
                                  // other keys are unhandled
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            cell.value
                          )}
                        </FixedRowHeaderCell>
                      )
                  )}

                  {(sortedData.records[rowIndex] ?? []).map((cell, cellIndex) => {
                    const arrowDirection = sortedData.metadata?.arrowDirection?.[rowIndex]?.[cellIndex];
                    const arrowColor = sortedData.metadata?.arrowColors?.[rowIndex]?.[cellIndex];

                    const isSelected = isCellSelected(
                      rowIndex,
                      cellIndex,
                      isRowSelected,
                      selection,
                      isDragging,
                      dragMode,
                      dragStart,
                      dragEnd
                    );

                    return (
                      <SelectableCell
                        key={cellIndex}
                        data-row-index={rowIndex}
                        data-cell-index={cellIndex}
                        $isSelected={isSelected}
                        $selectionColor={selectionBg}
                        $selectionBorder={selectionBorder}
                        onMouseDown={(e: React.MouseEvent) => {
                          if (!props.disableSelection) {
                            e.stopPropagation();
                            handleCellMouseDown(rowIndex, cellIndex, e);
                          }
                        }}
                        onMouseEnter={(e: React.MouseEvent) => {
                          if (isDragging && dragMode === 'cell' && dragStart && !props.disableSelection) {
                            setDragEnd({ row: rowIndex, col: cellIndex });
                          }
                        }}
                        onClick={(e: React.MouseEvent) => {
                          if (!props.disableSelection) {
                            e.stopPropagation();
                            handleCellClick(rowIndex, cellIndex, e);
                          }
                        }}
                      >
                        <CellContainer>
                          <span>{roundCellValue(cell, effectiveDecimalPlaces)}</span>
                          {arrowDirection &&
                            arrowDirection !== 'None' &&
                            arrowColor !== undefined &&
                            renderArrowIcon(arrowDirection, arrowColor)}
                        </CellContainer>
                      </SelectableCell>
                    );
                  })}
                </SelectableRow>
              );
            })}
          </tbody>
        </TableElement>
      </TableContainer>
    );
  })
);

AnalysisTable.displayName = 'AnalysisTable';

const TableContainer = styled.div(({ theme }) => ({
  overflow: 'auto',
  position: 'relative',
  maxHeight: '80vh',
  border: `1px solid ${theme?.palette?.gray?.light1 || 'var(--gray-l)'}`,
  borderRadius: '4px',
}));

const TableElement = styled.table<{ headerBackgroundColor?: string }>(({ headerBackgroundColor, theme }) => ({
  backgroundColor: theme.palette.white.main,
  '--header-bg-color': headerBackgroundColor || theme.palette.gray.light3,
}));

const CellContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ArrowIcon = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const SortIconContainer = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;

  th:hover & {
    opacity: 1;
  }
`;

const SortableHeaderCell = styled.th<{
  $freezeColumns: boolean;
  $rowIndex: number;
  $isSortable: boolean;
}>(({ theme, $isSortable }) => ({
  ...($isSortable && {
    cursor: 'pointer',
    userSelect: 'none',
  }),
  '&:hover': {
    backgroundColor: theme?.palette?.gray?.light3 ? `${theme.palette.gray.light3}80` : 'rgba(0, 0, 0, 0.02)',
  },
}));

const FixedRowHeaderCell = styled.td<{
  $freezeRows: boolean;
  $colIndex: number;
  $rowNestingDepth: number;
}>(({ theme, $freezeRows }) => ({
  ...($freezeRows && {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    marginLeft: '-1px',
    borderRight: `3px solid ${theme?.palette?.gray?.main || '#adadad'}`,
  }),
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme?.palette?.gray?.light3 ? `${theme.palette.gray.light3}80` : 'rgba(0, 0, 0, 0.02)',
  },
}));

const FixedCornerCell = styled.th<{
  $freezeColumns: boolean;
  $freezeRows: boolean;
  $colIndex: number;
  $rowIndex: number;
  $rowNestingDepth: number;
}>(({ theme, $freezeRows, $freezeColumns }) => ({
  ...($freezeRows &&
    !$freezeColumns && {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      marginLeft: '-1px',
      borderRight: `3px solid ${theme?.palette?.gray?.main || '#adadad'}`,
    }),
}));

const SelectableRow = styled.tr<{ $isSelected: boolean; $selectionColor: string; $selectionBorder: string }>`
  ${(props) =>
    props.$isSelected &&
    `
    background-color: ${props.$selectionColor};
    border-left: 3px solid ${props.$selectionBorder};
  `}
  cursor: pointer;
  transition: background-color 0.15s ease;
  user-select: none;
`;

const _SelectableColumn = styled.th<{
  $isSelected: boolean;
  $selectionColor: string;
  $selectionBorder: string;
}>`
  ${(props) =>
    props.$isSelected &&
    `
    background-color: ${props.$selectionColor};
    border-bottom: 3px solid ${props.$selectionBorder};
  `}
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected ? props.$selectionColor : `${props.$selectionColor}80`};
  }
`;

const SelectableSortableHeaderCell = styled(SortableHeaderCell)<{
  $isSelected: boolean;
  $selectionColor: string;
  $selectionBorder: string;
}>`
  ${(props) =>
    props.$isSelected &&
    `
    background-color: ${props.$selectionColor};
    border-bottom: 3px solid ${props.$selectionBorder};
  `}
  transition: background-color 0.15s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected ? props.$selectionColor : `${props.$selectionColor}80`};
  }
`;

const SelectableCell = styled.td<{ $isSelected: boolean; $selectionColor: string; $selectionBorder: string }>`
  ${(props) =>
    props.$isSelected &&
    `
    background-color: ${props.$selectionColor};
    outline: 2px solid ${props.$selectionBorder};
    outline-offset: -2px;
  `}
  cursor: cell;
  transition:
    background-color 0.15s ease,
    outline 0.15s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected ? props.$selectionColor : `${props.$selectionColor}80`};
  }
`;

const hexToRgba = (hex: string, alpha: number): string => {
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return `rgba(40, 170, 225, ${alpha})`;
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const EditableInput = styled.input(({ theme }) => {
  const primaryColor = theme?.palette?.primary?.main || '#28aae1';
  const bgColor = theme?.palette?.white?.main || 'white';
  return {
    flex: 1,
    minWidth: '150px',
    border: `2px solid ${primaryColor}`,
    borderRadius: '2px',
    padding: '2px 4px',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    backgroundColor: bgColor,
    outline: 'none',
    '&:focus': {
      borderColor: primaryColor,
      boxShadow: `0 0 0 2px ${hexToRgba(primaryColor, 0.2)}`,
    },
  };
});
